import type { User } from '@/stores/auth-store';
import { ApiError } from './client';

export interface LoginRequest {
  email: string;
  password: string;
}

// Backend response format (wrapped in data)
// M-005: refreshToken is now set as httpOnly cookie, body field is empty string
interface LoginApiResponse {
  data: {
    accessToken: string;
    refreshToken: string; // Always '' — actual token is in httpOnly cookie
    expiresIn: number;
    user: {
      id: string;
      email: string;
      name: string;
      role: string;
      tenantId?: string;
    };
  };
}

// Frontend-facing response format
// M-005: refreshToken removed — it's in httpOnly cookie, invisible to JS
export interface LoginResponse {
  accessToken: string;
  user: User;
}

export interface LogoutResponse {
  message: string;
}

export const authApi = {
  /**
   * Login with email/password.
   * M-005 SECURITY FIX: The login request uses `credentials: 'include'` so the browser
   * stores the httpOnly refresh token cookie set by the backend.
   * Only the access token (short-lived) is returned in the response body.
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    // credentials: 'include' is needed so the browser accepts the Set-Cookie header
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      // A hibát ApiError-ré alakítjuk, hogy a statusCode + code a hívóhoz (LoginPage) eljusson
      // → differenciált hibaüzenet (401 rossz-jelszó vs 5xx rendszerhiba vs 429 lockout).
      const error = (await response.json().catch(() => ({}))) as {
        message?: string;
        code?: string;
        error?: { message?: string; code?: string };
      };
      const code = error.error?.code ?? error.code;
      const message = error.message ?? error.error?.message ?? `HTTP ${response.status}`;
      throw new ApiError(message, code, response.status);
    }

    const body: LoginApiResponse = await response.json();
    // Unwrap the data property from backend response
    return {
      accessToken: body.data.accessToken,
      user: {
        id: body.data.user.id,
        email: body.data.user.email,
        name: body.data.user.name,
        role: body.data.user.role,
        tenantId: body.data.user.tenantId,
      },
    };
  },

  /**
   * Logout from current device.
   * M-005: credentials: 'include' sends the httpOnly refresh token cookie,
   * which the backend reads and revokes. The cookie is also cleared by Set-Cookie.
   */
  logout: async (): Promise<LogoutResponse> => {
    const { accessToken, user } = (await import('@/stores/auth-store')).useAuthStore.getState();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (user?.tenantId) {
      headers['X-Tenant-ID'] = user.tenantId;
    }

    const response = await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = (await response.json().catch(() => ({}))) as {
        message?: string;
        code?: string;
      };
      throw new ApiError(error.message ?? `HTTP ${response.status}`, error.code, response.status);
    }

    return response.json();
  },
};
